<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import sanitizeHtml from 'sanitize-html'

interface FormStateLike {
  initialValues?: {
    description?: string
  }
  setFieldValue?: (field: string, value: string) => void
  fields?: {
    description?: {
      states?: {
        error?: {
          message?: string
        }
      }
    }
  }
}

interface Props {
  form: FormStateLike
  editable: boolean
}

const props = defineProps<Props>()
const description = ref('')

const initValues = computed(() => props.form?.initialValues || {})
const sanitizedDescription = computed(() => sanitizeHtml(description.value || ''))
const quillToolbar = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  [{ font: [] }],
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
  ['link', 'image', 'video'],
  ['formula', 'code-block'],
  ['clean'],
]

watch(
  () => initValues.value,
  (newVal: { description?: string }) => {
    description.value = newVal?.description || ''
    props.form?.setFieldValue?.('description', description.value)
  },
  { immediate: true },
)

watch(description, (val) => {
  props.form?.setFieldValue?.('description', val || '')
})
</script>

<template>
  <div class="estate-project-section-body">
    <InputText type="text" name="description" hidden />

    <label class="estate-project-section-title">Nội dung mô tả <span>*</span></label>

    <div class="estate-project-editor-shell">
      <template v-if="editable">
        <QuillEditor
          v-model:content="description"
          contentType="html"
          theme="snow"
          :toolbar="quillToolbar"
          placeholder="Nhập địa chỉ dự án"
        />
      </template>
      <template v-else>
        <div v-html="sanitizedDescription" class="estate-project-editor-preview"></div>
      </template>
    </div>

    <span class="estate-project-error">{{ props.form?.fields?.description?.states?.error?.message }}</span>
  </div>
</template>

<style scoped>
.estate-project-section-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.estate-project-section-title {
  font-size: 16px;
  font-weight: 700;
  color: #000;
}

.estate-project-section-title span {
  color: #ff0000;
}

.estate-project-editor-shell {
  background: #f7f7f7;
  border-radius: 4px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  min-height: 282px;
}

:deep(.estate-project-editor-shell .ql-toolbar.ql-snow) {
  background: #ffffff;
  border: 0;
  border-bottom: 1px solid #e5e7eb;
  min-height: 53px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
}

:deep(.estate-project-editor-shell .ql-formats) {
  margin-right: 10px;
}

:deep(.estate-project-editor-shell .ql-picker),
:deep(.estate-project-editor-shell .ql-toolbar button) {
  color: #111827;
}

:deep(.estate-project-editor-shell .ql-container.ql-snow) {
  border: 0;
  min-height: 229px;
  background: #f7f7f7;
}

:deep(.estate-project-editor-shell .ql-editor) {
  min-height: 229px;
  padding: 16px;
  font-size: 16px;
  color: #111827;
}

:deep(.estate-project-editor-shell .ql-editor.ql-blank::before) {
  left: 16px;
  right: 16px;
  font-style: normal;
  font-size: 30px;
  color: #777777;
}

.estate-project-editor-preview {
  min-height: 229px;
  padding: 16px;
  font-size: 16px;
  color: #111827;
}

:deep(.estate-project-editor-shell .ql-tooltip) {
  z-index: 20;
}

.estate-project-error {
  display: block;
  font-size: 12px;
  font-style: italic;
  color: #dc2626;
}
</style>
